<template>
  <div class="billing-container" :class="{ 'flow-step--readonly': viewOnly }">
    <div v-if="false" class="billing-shared-actions">
      <base-button
        :loading="generatingBillRequest"
        :disabled="generatingBillRequest"
        size="sm"
        type="primary"
        @click="createBillRequest()"
        >Bill Request</base-button
      >
    </div>

    <div class="billing-step__cards">
      <article class="billing-mobile-card billing-mobile-card--vendor">
        <header class="billing-mobile-card__header">
          <div class="billing-mobile-card__icon billing-mobile-card__icon--vendor">
            <i class="fas fa-tools" aria-hidden="true" />
          </div>
          <div class="billing-mobile-card__titles">
            <h3 class="billing-mobile-card__title">{{ vendorCardTitle }}</h3>
            <p class="billing-mobile-card__subtitle">Bill</p>
          </div>
          <button
            v-if="!viewOnly"
            type="button"
            class="billing-mobile-card__link"
            @click="getVendorInvoiceProducts()"
          >
            + Bill
          </button>
        </header>

        <div class="billing-mobile-card__stats">
          <div class="billing-stat-row">
            <span>Bill amount</span>
            <span>{{ formatBillingAmount(vendorLaborTotal) }}</span>
          </div>
          <div class="billing-stat-row">
            <span>External goods</span>
            <span>{{ formatBillingAmount(vendorExternalGoodsAmount) }}</span>
          </div>
          <div class="billing-stat-row billing-stat-row--total">
            <span>Total</span>
            <span>{{ formatBillingAmount(vendorBillTotal) }}</span>
          </div>
        </div>

        <div class="billing-mobile-card__details">
          <div class="overflow-x-auto">
            <table class="labor-table">
              <thead>
                <tr>
                  <th class="col-desc">Description</th>
                  <th class="col-cost">Cost (₹)</th>
                  <th class="col-qty">Qty</th>
                  <th class="col-disc">Disc</th>
                  <th class="col-total">Total</th>
                  <th v-if="false" class="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="serviceRequestLabors.length === 0">
                  <td :colspan="viewOnly ? 5 : 6" class="empty-row">
                    No labor entries yet.
                  </td>
                </tr>
                <tr
                  v-for="row in serviceRequestLabors"
                  :key="row.serviceRequestLaborId"
                >
                  <td class="col-desc" :title="row.description">
                    {{ row.description }}
                  </td>
                  <td class="col-cost">
                    <div
                      v-if="!viewOnly && row.isLaborEditable"
                      class="cost-edit-wrap"
                    >
                      <input
                        v-model="row.laborCost"
                        type="number"
                        class="cost-input"
                      />
                      <button class="btn-save" @click="updateLaborCost(row)">
                        Save
                      </button>
                    </div>
                    <div v-else-if="!viewOnly" class="cost-view-wrap">
                      <span>{{ row.laborCost }}</span>
                      <button
                        class="btn-edit"
                        @click="row.isLaborEditable = true"
                      >
                        Edit
                      </button>
                    </div>
                    <span v-else>{{ row.laborCost }}</span>
                  </td>
                  <td class="col-qty">
                    <div v-if="!viewOnly" class="qty-wrap">
                      <button class="qty-btn" @click="decrementQty(row)">
                        −
                      </button>
                      <input
                        v-model.number="row.quantity"
                        type="number"
                        min="1"
                        class="qty-input"
                        @change="updateQuantity(row)"
                      />
                      <button class="qty-btn" @click="incrementQty(row)">
                        +
                      </button>
                    </div>
                    <span v-else>{{ row.quantity }}</span>
                  </td>
                  <td class="col-disc">{{ row.discount || 0 }}</td>
                  <td class="col-total">{{ calculateTotal(row) }}</td>
                  <td v-if="false" class="col-action">
                    <button class="btn-delete" @click="deleteLabor(row)">
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="serviceRequestLabors.length > 0">
                <tr>
                  <td colspan="4" class="total-label">Labor Total</td>
                  <td class="total-value">{{ vendorLaborTotal }}</td>
                  <td v-if="!viewOnly"></td>
                </tr>
              </tfoot>
            </table>
          </div>

        <div
          v-if="assistants.length > 0"
          class="assistant-section"
        >
            <div class="assistant-header">
              <div>
                <span class="assistant-title">Helper / Assistant Labor Share</span>
                <span class="vendor-labor-hint">
                  — Vendor Labor Total:
                  <strong>₹{{ vendorLaborTotal }}</strong>
                </span>
              </div>
              <base-button
                v-if="!viewOnly"
                size="sm"
                type="primary"
                :loading="savingAssistantLabor"
                :disabled="savingAssistantLabor"
                @click="saveAssistantLabor()"
                >Update Share</base-button
              >
            </div>
            <div class="assistant-rows">
              <div
                v-for="a in assistants"
                :key="a.serviceRequestAssistantId"
                class="assistant-row"
              >
                <span class="assistant-name">{{ a.name }}</span>
                <span class="assistant-badge">{{ a.type }}</span>
                <span v-if="viewOnly" class="assistant-readonly-amount"
                  >₹{{ a.labor || 0 }}</span
                >
                <div v-else class="assistant-input-wrap">
                  <span class="rupee-sign">₹</span>
                  <input
                    v-model="a.labor"
                    type="number"
                    min="0"
                    class="assistant-amount-input"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <article class="billing-mobile-card billing-mobile-card--rajput">
        <header class="billing-mobile-card__header">
          <div class="billing-mobile-card__icon billing-mobile-card__icon--rajput">
            <i class="fas fa-box" aria-hidden="true" />
          </div>
          <div class="billing-mobile-card__titles">
            <h3 class="billing-mobile-card__title">{{ branchDisplayName }}</h3>
            <p class="billing-mobile-card__subtitle">Rajput bill</p>
          </div>
        </header>

        <div class="billing-mobile-card__stats">
          <div class="billing-stat-row billing-stat-row--total">
            <span>Total amount</span>
            <span>{{ formatBillingAmount(rajputBillingTotal) }}</span>
          </div>
          <div class="billing-stat-row">
            <span>Status</span>
            <span>{{ rajputBillingStatusLabel }}</span>
          </div>
        </div>

        <div v-if="false" class="billing-mobile-card__details billing-rajput-actions">
            <base-button
              v-if="serviceRequest.saleId"
              type="success"
              size="sm"
              @click="getSaleInvoiceProducts()"
              >✓ Sale Invoice</base-button
            >
            <base-button
              v-else
              size="sm"
              type="primary"
              @click="getSaleInvoiceProducts()"
              >Gen. Sale Invoice</base-button
            >
        </div>
      </article>
    </div>

    <!-- Vendor Invoice Dialog -->
    <el-dialog :visible.sync="showVendorInvoiceData" title="Vendor Invoice">
      <el-table
        :data="vendorInvoiceData.labors"
        :show-summary="true"
        :summary-method="getTotalOfVendorInvoice"
        height="350"
      >
        <el-table-column label="Name">
          <template slot-scope="scope">
            <div>{{ scope.row.description }}</div>
            <div>Qty: {{ scope.row.quantity }}</div>
            <div>Disc: {{ scope.row.discount }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Price">
          <template slot-scope="scope">
            {{ scope.row.laborCost | currency }}
          </template>
        </el-table-column>
        <el-table-column label="Total">
          <template slot-scope="scope">
            {{ scope.row.total | currency }}
          </template>
        </el-table-column>
        <template slot="append">
          <tr>
            <td>
              External Goods Total:
              {{ vendorInvoiceData.externalGoodsTotal | currency }}
            </td>
          </tr>
        </template>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="!viewOnly && !serviceRequest.vendorInvoice"
          type="primary"
          @click="generateVendorsInvoice()"
        >
          Generate Vendor's Invoice
        </el-button>
      </span>
    </el-dialog>

    <!-- Sale Invoice Dialog -->
    <el-dialog
      :visible.sync="showSaleInvoiceProducts"
      title="Sale Invoice Products"
      width="80%"
    >
      <el-table
        :data="saleInvoiceProducts"
        :show-summary="true"
        :summary-method="getSummary"
        sum-text="Total"
        height="350"
      >
        <el-table-column label="Name">
          <template slot-scope="scope">{{ scope.row.name }}</template>
        </el-table-column>
        <el-table-column label="Qty">
          <template slot-scope="scope">{{ scope.row.quantity }}</template>
        </el-table-column>
        <el-table-column label="Price">
          <template slot-scope="scope">{{ scope.row.price }}</template>
        </el-table-column>
        <el-table-column label="Disc.">
          <template slot-scope="scope">
            <span v-if="scope.row.isPercentage">{{ scope.row.discount }}%</span>
            <span v-else>{{ scope.row.discount | currency }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Tax Rate">
          <template slot-scope="scope">{{ scope.row.taxRate }}%</template>
        </el-table-column>
        <el-table-column label="Total">
          <template slot-scope="scope">{{ scope.row.total | currency }}</template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="!viewOnly && !serviceRequest.saleId"
          type="primary"
          @click="generateSale()"
        >
          Generate Invoice
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import {
  Select,
  Option,
  Table,
  TableColumn,
  Tag,
  Dialog,
  Button
} from "element-ui";
import endpoints from "@/utils/constants";
import { apiHelper } from "@/utils/helpers";
import branchDetailsMixin from "@/mixins/branchDetailsMixin";
import { vendorDisplayName } from "@/utils/serviceRequestFlowHelpers";
import serviceRequestFlowViewOnlyMixin from "@/mixins/serviceRequestFlowViewOnlyMixin";

export default {
  mixins: [branchDetailsMixin, serviceRequestFlowViewOnlyMixin],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Dialog.name]: Dialog,
    [Button.name]: Button
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      })
        .format(amount)
        .replace(".00", "");
    }
  },
  data: () => ({
    selectedLabor: {},
    vendorInvoiceData: {},
    showVendorInvoiceData: false,
    saleInvoiceProducts: [],
    showSaleInvoiceProducts: false,
    generatingBillRequest: false,
    assistants: [],
    savingAssistantLabor: false,
    assistantsFetched: false
  }),
  computed: {
    ...mapState("laborCost", ["laborCosts"]),
    ...mapState("serviceRequest", ["serviceRequest"]),
    ...mapState("serviceRequestChalan", [
      "serviceRequestChalan",
      "serviceRequestChalansList"
    ]),
    ...mapState("serviceRequestLabor", [
      "serviceRequestLabors",
      "serviceRequestLabor"
    ]),
    ...mapGetters({
      statusList: "status/getStatusList",
      serviceRequestTransactions:
        "serviceRequestTransaction/getServiceRequestTransactions"
    }),
    hasChalans() {
      return (
        this.serviceRequestChalansList &&
        this.serviceRequestChalansList.length > 0
      );
    },
    vendorLaborTotal() {
      return this.serviceRequestLabors.reduce(
        (sum, l) => sum + l.laborCost * l.quantity - (l.discount || 0),
        0
      );
    },
    vendorName() {
      return vendorDisplayName(this.serviceRequest);
    },
    vendorCardTitle() {
      const vendor = this.serviceRequest && this.serviceRequest.vendor;
      if (!vendor || typeof vendor !== "object") return this.vendorName;
      const firm = (vendor.firmName || vendor.firm_name || "").trim();
      if (firm) return firm;
      return this.vendorName;
    },
    vendorExternalGoodsAmount() {
      const fromRequest = parseFloat(this.serviceRequest.externalGoodsTotal);
      if (!isNaN(fromRequest)) return fromRequest;
      const meta = this.serviceRequest.externalGoodsMeta;
      if (!Array.isArray(meta)) return 0;
      return meta.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);
    },
    vendorBillTotal() {
      return this.vendorLaborTotal + (this.vendorExternalGoodsAmount || 0);
    },
    rajputBillingTotal() {
      const list = this.serviceRequestChalansList || [];
      return list.reduce(
        (sum, chalan) => sum + this.chalanRowAmount(chalan),
        0
      );
    },
    rajputBillingStatusLabel() {
      if (this.serviceRequest.saleId) {
        return "Sale invoice generated";
      }
      const billRequest = this.serviceRequest.billRequest;
      if (billRequest && typeof billRequest === "object") {
        if (billRequest.statusLabel) return billRequest.statusLabel;
        if (billRequest.status) return this.humanizeBillStatus(billRequest.status);
      }
      const direct =
        this.serviceRequest.billRequestStatus ||
        this.serviceRequest.billRequestStatusLabel;
      if (direct) return String(direct);
      if (
        this.serviceRequest.isBillRequested ||
        this.serviceRequest.billRequested
      ) {
        return "Request sent";
      }
      return "Not requested";
    }
  },
  watch: {
    "serviceRequest.vendorId": {
      immediate: true,
      handler(id) {
        if (id && !this.assistantsFetched) {
          this.fetchAssistants();
        }
      }
    }
  },
  mounted() {
    this.loadBranchDetails();
    this.getLaborCosts();
    this.getServiceRequestLabors(this.$route.params.id);
    this.getServiceRequestChalans(this.$route.params.id);
  },
  methods: {
    ...mapActions({
      getLaborCosts: "laborCost/getLaborCosts",
      getServiceRequestLabors: "serviceRequestLabor/getServiceRequestLabors",
      saveServiceRequestLabor: "serviceRequestLabor/saveServiceRequestLabor",
      updateLabor: "serviceRequestLabor/updateServiceRequestLabor",
      generateServiceRequestSale: "serviceRequest/generateSale",
      fetchServiceRequest: "serviceRequest/fetchServiceRequest",
      generateVendorsInvoiceNumber: "serviceRequest/generateVendorsInvoiceNumber",
      deleteServiceRequestLabor: "serviceRequestLabor/deleteServiceRequestLabor",
      getVendorInvoiceData: "serviceRequest/getVendorInvoiceData",
      fetchSaleInvoiceProducts: "serviceRequest/getSaleInvoiceProducts",
      generateBillRequest: "serviceRequest/generateBillRequest",
      getServiceRequestChalans: "serviceRequestChalan/getServiceRequestChalans"
    }),
    ...mapMutations({
      setServiceRequestLabor: "serviceRequestLabor/setServiceRequestLabor"
    }),

    chalanRowAmount(chalan) {
      if (!chalan || typeof chalan !== "object") return 0;
      const total = parseFloat(chalan.total);
      if (!isNaN(total) && total) return total;
      const grandTotal = parseFloat(chalan.grandTotal);
      if (!isNaN(grandTotal) && grandTotal) return grandTotal;
      const items = chalan.chalanItems;
      if (!Array.isArray(items)) return 0;
      return items.reduce(
        (sum, item) =>
          sum + (parseFloat(item.amount) || parseFloat(item.total) || 0),
        0
      );
    },
    formatBillingAmount(amount) {
      const value = parseFloat(amount) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0
      }).format(value);
    },
    humanizeBillStatus(status) {
      const raw = String(status || "").trim();
      if (!raw) return "Not requested";
      if (raw === "pending") return "Request sent";
      return raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, " ");
    },

    addServiceRequestLabors(item) {
      if (this.viewOnly) return;
      if (this.serviceRequest.status.code === "completed") {
        this.$notify({
          message: "Cannot add labor on a completed service request.",
          type: "danger",
          title: "Failure"
        });
        return;
      }
      if (!item || !item.cost) {
        this.$notify({ message: "Cost Not Added!", type: "danger", title: "Failure" });
        return;
      }
      this.setServiceRequestLabor({
        quantity: 1,
        laborCost: item.cost,
        serviceRequestId: this.$route.params.id,
        description: item.description,
        vendorId: this.serviceRequest.vendorId
      });
      this.saveServiceRequestLabor()
        .then(res => {
          if (res.status === 201) {
            this.selectedLabor = {};
            this.$notify({ message: "Labor Added!", type: "success", title: "Success" });
          }
          this.getServiceRequestLabors(this.$route.params.id);
        })
        .catch(() => {
          this.$notify({ message: "Try Again!", type: "danger", title: "Failure" });
        });
    },

    incrementQty(row) {
      if (this.viewOnly) return;
      row.quantity = (row.quantity || 1) + 1;
      this.updateQuantity(row);
    },
    decrementQty(row) {
      if (this.viewOnly) return;
      if (row.quantity > 1) {
        row.quantity = row.quantity - 1;
        this.updateQuantity(row);
      }
    },
    async updateQuantity(row) {
      if (this.viewOnly) return;
      await this.updateLabor({
        quantity: row.quantity,
        serviceRequestId: this.$route.params.id,
        serviceRequestLaborId: row.serviceRequestLaborId
      });
      this.$notify({ message: "Quantity Updated!", type: "success", title: "Success" });
    },
    updateLaborCost(row) {
      if (this.viewOnly) return;
      this.updateLabor({
        laborCost: row.laborCost,
        serviceRequestId: this.$route.params.id,
        serviceRequestLaborId: row.serviceRequestLaborId
      }).then(() => {
        row.isLaborEditable = false;
        this.getServiceRequestLabors(this.$route.params.id);
        this.$notify({ message: "Labor Updated!", type: "success", title: "Success" });
      });
    },
    calculateTotal(row) {
      return row.laborCost * row.quantity - (row.discount || 0);
    },
    deleteLabor(row) {
      if (this.viewOnly) return;
      this.deleteServiceRequestLabor({
        serviceRequestId: row.serviceRequestId,
        serviceRequestLaborId: row.serviceRequestLaborId
      }).then(res => {
        if (res.status === 201) {
          this.getServiceRequestLabors(this.$route.params.id);
          this.$notify({ message: "Labor Deleted!", type: "success", title: "Success" });
        }
      });
    },

    async fetchAssistants() {
      const vendorId = this.serviceRequest && this.serviceRequest.vendorId;
      if (!vendorId) return;
      this.assistantsFetched = true;
      const srId = this.$route.params.id;
      try {
        const res = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vendors/${vendorId}/service-requests/${srId}/assistants`
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        this.assistants = rows
          .filter(r => (r.vendor_id || r.vendorId || "").trim())
          .map(r => ({
            serviceRequestAssistantId: (
              r.service_request_assistant_id ||
              r.serviceRequestAssistantId ||
              r.id ||
              ""
            ).toString().trim(),
            vendorId: (r.vendor_id || r.vendorId || "").toString().trim(),
            name: (
              r.name || r.vendor_name || r.vendorName ||
              r.assistant_name || r.assistantName || "—"
            ).toString().trim(),
            type: (r.type || r.assistant_type || r.assistantType || "helper")
              .toString().trim().toLowerCase(),
            labor: String(r.labor || r.assistant_labor || r.assistantLabor || "0")
          }));
      } catch (_) {
        // No assistants or endpoint unavailable
      }
    },

    async saveAssistantLabor() {
      if (this.viewOnly) return;
      const vendorId = this.serviceRequest && this.serviceRequest.vendorId;
      if (!vendorId) return;
      this.savingAssistantLabor = true;
      const payload = {
        assistants: this.assistants
          .filter(a => a.serviceRequestAssistantId && a.vendorId)
          .map(a => ({
            service_request_assistant_id: a.serviceRequestAssistantId,
            vendor_id: a.vendorId,
            assistant_type: a.type || "helper",
            labor: String(a.labor || "0")
          }))
      };
      try {
        await apiHelper.call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vendors/${vendorId}/service-requests/${this.$route.params.id}/assistants/labor`,
          payload
        );
        this.$notify({ message: "Assistant labor share updated!", type: "success", title: "Success" });
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          "Failed to update. Please try again.";
        this.$notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.savingAssistantLabor = false;
      }
    },

    async getSaleInvoiceProducts() {
      if (this.viewOnly) return;
      const pendingChalan =
        this.serviceRequestChalansList &&
        this.serviceRequestChalansList.find(
          i => i.status.label === "Pending" || i.status.label === "Dispatched"
        );
      if (!this.serviceRequestChalansList || !this.serviceRequestChalansList.length) {
        this.$notify({ message: "No Chalan Added!", type: "warning", title: "Warning" });
      } else if (pendingChalan) {
        this.$notify({
          message: "Chalans are Pending or Dispatched — sale can't be generated.",
          type: "warning",
          title: "Warning"
        });
      } else {
        this.saleInvoiceProducts = await this.fetchSaleInvoiceProducts(
          this.serviceRequest.serviceRequestId
        );
        this.showSaleInvoiceProducts = true;
      }
    },
    getSummary(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((col, idx) => {
        if (idx === 0) { sums[idx] = "Total"; return; }
        if (idx === 5) {
          sums[idx] = "Rs. " + data.reduce((a, b) => a + b.total, 0);
        }
      });
      return sums;
    },
    generateSale() {
      if (this.viewOnly) return;
      this.generateServiceRequestSale(this.$route.params.id)
        .then(() => {
          this.$notify({ message: "Sale Generated!", type: "success", title: "Success" });
          this.showSaleInvoiceProducts = false;
          this.fetchServiceRequest(this.$route.params.id);
        })
        .catch(() => {
          this.$notify({ message: "Error generating sale. Try again.", type: "danger", title: "Error" });
        });
    },
    createBillRequest() {
      if (this.viewOnly) return;
      if (!this.hasChalans || this.generatingBillRequest) return;
      this.generatingBillRequest = true;
      this.generateBillRequest(this.$route.params.id)
        .then(() => {
          this.$notify({ message: "Bill request created!", type: "success", title: "Success" });
        })
        .catch(err => {
          this.$notify({
            message:
              (err.response && err.response.data && err.response.data.message) ||
              "Error creating bill request.",
            type: "danger",
            title: "Error"
          });
        })
        .finally(() => { this.generatingBillRequest = false; });
    },
    async getVendorInvoiceProducts() {
      if (this.viewOnly) return;
      this.vendorInvoiceData = await this.getVendorInvoiceData(
        this.serviceRequest.serviceRequestId
      );
      this.showVendorInvoiceData = true;
    },
    getTotalOfVendorInvoice(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((col, idx) => {
        if (idx === 0) { sums[idx] = "Total"; return; }
        if (idx === 2) {
          let sum = data.reduce((a, b) => a + b.total, 0);
          sum += this.vendorInvoiceData.externalGoodsTotal || 0;
          sums[idx] = "Rs. " + sum;
        }
      });
      return sums;
    },
    generateVendorsInvoice() {
      if (this.viewOnly) return;
      if (
        this.serviceRequest.externalGoodMeta != {} ||
        this.serviceRequestLabors.length > 0
      ) {
        this.generateVendorsInvoiceNumber().then(res => {
          if (res) {
            this.$notify({ message: "Vendor Invoice Generated!", type: "success", title: "Success" });
            this.showVendorInvoiceData = false;
          }
        });
      } else {
        this.$notify({
          message: "No External Good/s or Labor Added.",
          type: "warning",
          title: "Warning"
        });
      }
    }
  }
};
</script>

<style scoped>
.billing-container {
  padding: 0 0 8px;
}
.billing-step__cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
.billing-labor-table {
  width: 100%;
  overflow: visible;
}
.billing-mobile-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.billing-mobile-card--vendor {
  border-color: #93c5fd;
  box-shadow: 0 0 0 1px #bfdbfe;
}
.billing-mobile-card--rajput {
  border-color: #fdba74;
  box-shadow: 0 0 0 1px #fed7aa;
}
.billing-mobile-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px 10px;
}
.billing-mobile-card__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.billing-mobile-card__icon--vendor {
  background: #dbeafe;
  color: #2563eb;
}
.billing-mobile-card__icon--rajput {
  background: #ffedd5;
  color: #ea580c;
}
.billing-mobile-card__titles {
  flex: 1;
  min-width: 0;
}
.billing-mobile-card__title {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  word-break: break-word;
}
.billing-mobile-card__subtitle {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}
.billing-mobile-card__link {
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;
}
.billing-mobile-card__link:hover {
  text-decoration: underline;
}
.billing-mobile-card__stats {
  margin: 0 14px 14px;
  padding: 4px 12px;
  background: #f3f4f6;
  border-radius: 8px;
}
.billing-stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}
.billing-stat-row:last-child {
  border-bottom: none;
}
.billing-stat-row span:last-child {
  font-weight: 600;
  color: #111827;
  text-align: right;
  max-width: 58%;
  word-break: break-word;
}
.billing-stat-row--total span:last-child {
  font-weight: 700;
  font-size: 15px;
}
.billing-mobile-card__details {
  padding: 12px 14px 14px;
  border-top: 1px solid #f3f4f6;
}
.billing-invoice-meta {
  display: block;
  margin-bottom: 10px;
  font-size: 12px;
  color: #6b7280;
}
.billing-shared-actions {
  margin-bottom: 12px;
}
.billing-inline-select {
  margin-bottom: 8px;
}
.billing-rajput-hint {
  font-size: 13px;
  color: #374151;
  margin: 0 0 8px;
}
.billing-rajput-muted {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

/* Top bar */
.billing-topbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}
.labor-select-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.select-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}
.billing-action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Labor table */
.labor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 0;
  table-layout: fixed;
}
.labor-table thead tr {
  background: #f3f4f6;
}
.labor-table th {
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}
.labor-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
  vertical-align: middle;
}
.labor-table tbody tr:hover {
  background: #f9fafb;
}
.labor-table tfoot td {
  padding: 8px 10px;
  border-top: 2px solid #e5e7eb;
  font-weight: 600;
}

/* Column widths */
.col-desc {
  width: 36%;
  max-width: none;
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
  word-break: break-word;
}
.col-cost { width: 16%; }
.col-qty  { width: 14%; }
.col-disc { width: 14%; white-space: nowrap; }
.col-total { width: 20%; white-space: nowrap; font-weight: 500; }
.total-label { text-align: right; color: #6b7280; font-size: 12px; }
.total-value { color: #111827; font-weight: 700; }

.empty-row {
  text-align: center;
  color: #9ca3af;
  padding: 24px 0;
  font-size: 13px;
}

/* Cost edit */
.cost-edit-wrap, .cost-view-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cost-input {
  width: 70px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 13px;
  outline: none;
}
.cost-input:focus { border-color: #3b82f6; }
.btn-edit {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #6366f1;
  background: #6366f1;
  color: #fff;
  cursor: pointer;
}
.btn-save {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #10b981;
  background: #10b981;
  color: #fff;
  cursor: pointer;
}

/* Qty controls */
.qty-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  width: fit-content;
}
.qty-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: #f3f4f6;
  font-size: 16px;
  cursor: pointer;
  color: #374151;
  line-height: 1;
}
.qty-btn:hover { background: #e5e7eb; }
.qty-input {
  width: 44px;
  height: 28px;
  border: none;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  text-align: center;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}
.qty-input::-webkit-inner-spin-button,
.qty-input::-webkit-outer-spin-button { -webkit-appearance: none; }

.btn-delete {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #ef4444;
  background: #ef4444;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.btn-delete:hover { background: #dc2626; }

/* Assistant section */
.assistant-section {
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f0fdf4;
  border-bottom: 1px solid #d1fae5;
}
.assistant-title {
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
}
.vendor-labor-hint {
  font-size: 12px;
  color: #6b7280;
  margin-left: 6px;
}
.assistant-rows {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.assistant-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.assistant-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  text-transform: capitalize;
  white-space: nowrap;
}
.assistant-input-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}
.rupee-sign {
  padding: 0 6px;
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  border-right: 1px solid #d1d5db;
  line-height: 28px;
  height: 28px;
}
.assistant-amount-input {
  width: 80px;
  height: 28px;
  border: none;
  outline: none;
  padding: 0 8px;
  font-size: 13px;
  color: #111827;
  -moz-appearance: textfield;
}
.assistant-amount-input::-webkit-inner-spin-button,
.assistant-amount-input::-webkit-outer-spin-button { -webkit-appearance: none; }
.assistant-readonly-amount {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
}
</style>
